import React from 'react'
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  FormHelperText,
} from '@mui/material'

export interface HeaderModalProps {
  open: boolean
  sentimentStatus: boolean
  thematicStatus: boolean
  onClose: () => void
  onSave: (values: any) => void
  sentimentAnalysis: boolean
  thematicAnalysis: boolean
}

export interface ToggleConfig {
  keyName: string
  label: string
  helperText: string
  analysisAvailable: boolean
  onText: string
  offText: string
}

// Reusable config provider
export const getAiToggleConfigs = (
  t: (key: string) => string,
  sentimentAnalysis: boolean,
  thematicAnalysis: boolean,
): ToggleConfig[] => [
  {
    keyName: 'sentiment',
    label: t('textAnalysis.sentimentToggle.title'),
    helperText:
      'Enable this feature to detect and display sentiment for all contributions using AI.',
    analysisAvailable: sentimentAnalysis,
    onText: 'On',
    offText: 'Off',
  },
  {
    keyName: 'thematic',
    label: 'Thematic analysis',
    helperText:
      'Enable this feature to identify and categorize common themes across all contributions.',
    analysisAvailable: thematicAnalysis,
    onText: 'On',
    offText: 'Off',
  },
]

interface ToggleSectionProps extends ToggleConfig {
  enabled: boolean
  disabledText: string
  onChange: (key: string, checked: boolean) => void
}

export const ToggleSection: React.FC<ToggleSectionProps> = ({
  keyName,
  label,
  helperText,
  analysisAvailable,
  disabledText,
  enabled,
  onChange,
  onText,
  offText,
}) => {
  const statusLabel = analysisAvailable && enabled ? onText : offText

  return (
    <Box
      sx={{ opacity: analysisAvailable ? 1 : 0.6, pb: 2 }}
      data-testid={`toggle-${keyName}`}
    >
      <FormControlLabel
        control={
          <Switch
            checked={analysisAvailable ? enabled : false}
            onChange={(e) => onChange(keyName, e.target.checked)}
            name={`${keyName}Switch`}
            inputProps={{ 'aria-label': `${label}` }}
            color="primary"
            disabled={!analysisAvailable}
          />
        }
        label={`${label}: ${statusLabel}`}
      />

      <FormHelperText>{helperText}</FormHelperText>
      {!analysisAvailable && (
        <Typography
          variant="body2"
          component="div"
          color="error.main"
          sx={{ fontStyle: 'italic', my: 0.5 }}
        >
          {disabledText}
        </Typography>
      )}
    </Box>
  )
}
